/**
 * Reusable Alpine.js component for buttons with loading state
 *
 * Usage:
 *
 * 1. Navigation button (shows spinner on click, navigates to href):
 *    <a href="/path" x-data="loadingButton()" @click="startLoading()">
 *        <template x-if="loading"><i data-lucide="loader-2" class="animate-spin"></i></template>
 *        <template x-if="!loading"><i data-lucide="icon-name"></i></template>
 *    </a>
 *
 * 2. AJAX toggle button (toggles state without page reload):
 *    <button x-data="loadingButton({ active: false, activeUrl: '/add', inactiveUrl: '/remove' })"
 *            @click="toggle()"
 *            :class="active ? 'active-class' : 'inactive-class'">
 *        <template x-if="loading"><i data-lucide="loader-2" class="animate-spin"></i></template>
 *        <template x-if="!loading"><i data-lucide="icon"></i></template>
 *    </button>
 */

export function loadingButton(config = {}) {
    return {
        loading: false,
        active: config.active ?? false,
        activeUrl: config.activeUrl ?? null,
        inactiveUrl: config.inactiveUrl ?? null,

        startLoading() {
            this.loading = true;
        },

        stopLoading() {
            this.loading = false;
        },

        async toggle() {
            if (this.loading) return;

            const url = this.active ? this.inactiveUrl : this.activeUrl;
            if (!url) return;

            this.loading = true;
            try {
                const response = await fetch(url + (url.includes('?') ? '&' : '?') + 'ajax=1');
                if (response.ok) {
                    this.active = !this.active;
                }
            } catch (e) {
                console.error('Toggle action failed:', e);
            } finally {
                this.loading = false;
            }
        },

        async action(url, onSuccess) {
            if (this.loading) return;

            this.loading = true;
            try {
                const response = await fetch(url + (url.includes('?') ? '&' : '?') + 'ajax=1');
                if (response.ok && onSuccess) {
                    onSuccess(response);
                }
            } catch (e) {
                console.error('Action failed:', e);
            } finally {
                this.loading = false;
            }
        }
    };
}
